import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
// The JSON data representation of the page
const pageData = {
  $schema: 'https://schema.org/Report',
  name: 'Andoe',
  frontmatter: {
    problem: 'Invisible in AI search',
    solution: 'Hire AI search agent',
    customers: 'Mid-Market Marketing Team'
  },
  hero: {
    kicker: 'Andoe / a team of AI agents, on retainer',
    headline: 'Get mentioned by ChatGPT and Google.',
    lede: 'AI models are your new sales team. We make sure they pitch you accurately, and often.',
    cta: {
      label: 'Deactivated'
    }
  },
  sections: [{
    ref: '01',
    title: 'Monitoring',
    deck: 'What the answer engines are saying, every week.',
    entries: [{
      ref: '1.1',
      label: 'Coverage',
      headline: 'Track the two LLMs that matter',
      body: "Track the two LLMs that matter, not all of them. Optimize for the majors and you're covered across the rest. Paying to monitor the long tail is a tax, not a strategy.",
      engines: ['ChatGPT', 'Google'],
      philosophy: 'optimize_for_majors'
    }, {
      ref: '1.2',
      label: 'Intent',
      headline: 'User intent tracking',
      body: 'See what real customers ask AI about your brand and category, and watch the questions shift week over week.',
      tracks: ['customer_questions', 'weekly_drift']
    }, {
      ref: '1.3',
      label: 'Cadence',
      headline: 'Weekly beats daily',
      body: 'LLMs are probabilistic, but they stay within contextual semantics. Daily tracking creates anxiety, not insight. Review weekly, then spend the rest of the week taking action. Andoe handles the analysis.',
      frequency: 'weekly',
      philosophy: 'weekly_beats_daily'
    }, {
      ref: '1.4',
      label: 'Benchmark',
      headline: 'Track two competitors',
      body: 'Side-by-side benchmarking across every prompt: citations, sentiment, and share of voice.',
      max_competitors: 2,
      metrics: ['citations', 'sentiment', 'share_of_voice']
    }, {
      ref: '1.5',
      label: 'Structure',
      headline: 'Prompt grouping',
      body: "Organize prompts by theme: pricing, comparisons, use-cases. Read each group's performance as its own narrative.",
      themes: ['pricing', 'comparisons', 'use_cases']
    }]
  }, {
    ref: '02',
    title: 'Intelligence',
    deck: 'From observations to a list of things to ship.',
    entries: [{
      ref: '2.1',
      label: 'Recommendations',
      headline: 'Detailed AEO, GEO, and SEO insights',
      body: "Automated weekly reports that show you what's happening, why it matters, and what to do next. As you complete each action item, the system learns, sharpening every insight that follows.",
      scope: ['AEO', 'GEO', 'SEO'],
      cadence: 'weekly',
      learning: 'continuous'
    }, {
      ref: '2.2',
      label: 'Coverage gaps',
      headline: 'Prompt suggestions',
      body: "AI-generated prompts that mirror how your audience actually asks, surfacing coverage gaps you didn't know you had.",
      source: 'audience_intent_model'
    }, {
      ref: '2.3',
      label: 'Playbook',
      headline: "Don't fully automate content with AI",
      body: 'Models are very good at recognizing model-generated text, and LLM providers have no incentive to reward it. They will always champion authentic content. Andoe gives you a step-by-step playbook with starter prompts so the work stays yours, just faster.',
      philosophy: 'human_authored_ai_assisted',
      provides: ['step_by_step_playbook', 'starter_prompts']
    }]
  }, {
    ref: '03',
    title: 'Team',
    deck: 'Built for whole teams, not single seats.',
    entries: [{
      ref: '3.1',
      label: 'Seats',
      headline: 'Unlimited seats',
      body: 'Bring marketing, SEO, content, and product. Pricing is per workspace, never per head.',
      pricing_model: 'per_workspace',
      per_seat_surcharge: false
    }, {
      ref: '3.2',
      label: 'Support',
      headline: 'Email support',
      body: 'A direct line to the team building Andoe.',
      channel: 'email'
    }]
  }],
  end_cta: {
    label: 'Next',
    headline: 'Hire your AI search analyst.',
    body: 'We are onboarding a small group of beta partners. Reach out and we will set up your workspace, run your first weekly visibility report, and walk you through the to-do list.',
    cta: {
      label: 'Deactivated'
    }
  }
};
function highlightJSON(obj: any, indent: number = 0): string {
  const pad = '  '.repeat(indent);
  const padNext = '  '.repeat(indent + 1);
  if (obj === null) return '<span class="b">null</span>';
  if (typeof obj === 'boolean') return '<span class="b">' + obj + '</span>';
  if (typeof obj === 'number') return '<span class="n">' + obj + '</span>';
  if (typeof obj === 'string') return '<span class="s">"' + obj.replace(/"/g, '\\"') + '"</span>';
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '<span class="p">[]</span>';
    const items = obj.map(v => padNext + highlightJSON(v, indent + 1));
    return '<span class="p">[</span>\n' + items.join('<span class="p">,</span>\n') + '\n' + pad + '<span class="p">]</span>';
  }
  if (typeof obj === 'object') {
    const keys = Object.keys(obj);
    if (keys.length === 0) return '<span class="p">{}</span>';
    const lines = keys.map(k => {
      return padNext + '<span class="k">"' + k + '"</span><span class="p">:</span> ' + highlightJSON(obj[k], indent + 1);
    });
    return '<span class="p">{</span>\n' + lines.join('<span class="p">,</span>\n') + '\n' + pad + '<span class="p">}</span>';
  }
  return String(obj);
}
export function LandingPage() {
  const [jsonHtml, setJsonHtml] = useState('');
  useEffect(() => {
    setJsonHtml('<span class="c">// the same context document, as ChatGPT and Google parse it</span>\n\n' + highlightJSON(pageData));
  }, []);
  return <>
      <Navigation data-id="element-163" />

      <main className="wrap report-main" data-id="element-164">
        <article className="report" data-id="element-165">
          {/* frontmatter */}
          <header className="frontmatter" data-id="element-166">
            <div className="fm-cell" data-id="element-167">
              <span className="lbl" data-id="element-168">Problem</span>
              <span className="val" data-id="element-169">Invisible in AI search</span>
            </div>
            <div className="fm-cell" data-id="element-170">
              <span className="lbl" data-id="element-171">Solution</span>
              <span className="val" data-id="element-172">Hire AI search agent</span>
            </div>
            <div className="fm-cell" data-id="element-173">
              <span className="lbl" data-id="element-174">Customers</span>
              <span className="val" data-id="element-175">Mid-Market Marketing Team</span>
            </div>
          </header>

          {/* HERO */}
          <section className="hero" data-id="element-176">
            <div className="kicker" data-id="element-177">
              Andoe / a team of AI agents, on retainer
            </div>
            <h1 data-id="element-178">Get mentioned by ChatGPT and Google.</h1>
            <p className="lede" data-id="element-179">
              AI models are your new sales team. We make sure they pitch you
              accurately, and often.
            </p>
            <span className="cta is-disabled" aria-disabled="true" data-id="element-180">
              Deactivated
            </span>
          </section>

          {/* 01 MONITORING */}
          <section className="report-section" data-id="element-181">
            <div className="section-head" data-id="element-182">
              <div className="section-num" data-id="element-183">01</div>
              <div className="section-title" data-id="element-184">
                <h2 data-id="element-185">Monitoring</h2>
                <span className="deck" data-id="element-186">
                  What the answer engines are saying, every week.
                </span>
              </div>
            </div>
            <div className="section-body" data-id="element-187">
              <div className="pad" data-id="element-188"></div>
              <div className="content" data-id="element-189">
                <div className="entry" data-id="element-190">
                  <div className="entry-label" data-id="element-191">
                    <span className="num" data-id="element-192">1.1</span> Coverage
                  </div>
                  <h3 data-id="element-193">Track the two LLMs that matter</h3>
                  <p data-id="element-194">
                    Track the two LLMs that matter, not all of them. Optimize
                    for the majors and you're{' '}
                    <strong data-id="element-195">covered across the rest</strong>. Paying to monitor
                    the long tail is a tax, not a strategy.
                  </p>
                </div>
                <div className="entry" data-id="element-196">
                  <div className="entry-label" data-id="element-197">
                    <span className="num" data-id="element-198">1.2</span> Intent
                  </div>
                  <h3 data-id="element-199">User intent tracking</h3>
                  <p data-id="element-200">
                    See what real customers ask AI about your brand and
                    category, and watch the questions shift week over week.
                  </p>
                </div>
                <div className="entry" data-id="element-201">
                  <div className="entry-label" data-id="element-202">
                    <span className="num" data-id="element-203">1.3</span> Cadence
                  </div>
                  <h3 data-id="element-204">Weekly beats daily</h3>
                  <p data-id="element-205">
                    LLMs are probabilistic, but they stay within{' '}
                    <strong data-id="element-206">contextual semantics</strong>. Daily tracking
                    creates anxiety, not insight. Review weekly, then spend the
                    rest of the week taking action. Andoe handles the analysis.
                  </p>
                </div>
                <div className="entry" data-id="element-207">
                  <div className="entry-label" data-id="element-208">
                    <span className="num" data-id="element-209">1.4</span> Benchmark
                  </div>
                  <h3 data-id="element-210">Track two competitors</h3>
                  <p data-id="element-211">
                    Side-by-side benchmarking across every prompt: citations,
                    sentiment, and share of voice.
                  </p>
                </div>
                <div className="entry" data-id="element-212">
                  <div className="entry-label" data-id="element-213">
                    <span className="num" data-id="element-214">1.5</span> Structure
                  </div>
                  <h3 data-id="element-215">Prompt grouping</h3>
                  <p data-id="element-216">
                    Organize prompts by theme: pricing, comparisons, use-cases.
                    Read each group's performance as its own narrative.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 02 INTELLIGENCE */}
          <section className="report-section" data-id="element-217">
            <div className="section-head" data-id="element-218">
              <div className="section-num" data-id="element-219">02</div>
              <div className="section-title" data-id="element-220">
                <h2 data-id="element-221">Intelligence</h2>
                <span className="deck" data-id="element-222">
                  From observations to a list of things to ship.
                </span>
              </div>
            </div>
            <div className="section-body" data-id="element-223">
              <div className="pad" data-id="element-224"></div>
              <div className="content" data-id="element-225">
                <div className="entry" data-id="element-226">
                  <div className="entry-label" data-id="element-227">
                    <span className="num" data-id="element-228">2.1</span> Recommendations
                  </div>
                  <h3 data-id="element-229">Detailed AEO, GEO, and SEO insights</h3>
                  <p data-id="element-230">
                    Automated weekly reports that show you what's happening, why
                    it matters, and what to do next. As you complete each action
                    item, the system learns, sharpening every insight that
                    follows.
                  </p>
                </div>
                <div className="entry" data-id="element-231">
                  <div className="entry-label" data-id="element-232">
                    <span className="num" data-id="element-233">2.2</span> Coverage gaps
                  </div>
                  <h3 data-id="element-234">Prompt suggestions</h3>
                  <p data-id="element-235">
                    AI-generated prompts that mirror how your audience actually
                    asks, surfacing coverage gaps you didn't know you had.
                  </p>
                </div>
                <div className="entry" data-id="element-236">
                  <div className="entry-label" data-id="element-237">
                    <span className="num" data-id="element-238">2.3</span> Playbook
                  </div>
                  <h3 data-id="element-239">Don't fully automate content with AI</h3>
                  <p data-id="element-240">
                    Models are very good at recognizing model-generated text,
                    and LLM providers have no incentive to reward it. They will
                    always champion <strong data-id="element-241">authentic content</strong>. Andoe
                    gives you a step-by-step playbook with starter prompts so
                    the work stays yours, just faster.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 03 TEAM */}
          <section className="report-section" data-id="element-242">
            <div className="section-head" data-id="element-243">
              <div className="section-num" data-id="element-244">03</div>
              <div className="section-title" data-id="element-245">
                <h2 data-id="element-246">Team</h2>
                <span className="deck" data-id="element-247">
                  Built for whole teams, not single seats.
                </span>
              </div>
            </div>
            <div className="section-body" data-id="element-248">
              <div className="pad" data-id="element-249"></div>
              <div className="content" data-id="element-250">
                <div className="entry" data-id="element-251">
                  <div className="entry-label" data-id="element-252">
                    <span className="num" data-id="element-253">3.1</span> Seats
                  </div>
                  <h3 data-id="element-254">Unlimited seats</h3>
                  <p data-id="element-255">
                    Bring marketing, SEO, content, and product. Pricing is per
                    workspace, never per head.
                  </p>
                </div>
                <div className="entry" data-id="element-256">
                  <div className="entry-label" data-id="element-257">
                    <span className="num" data-id="element-258">3.2</span> Support
                  </div>
                  <h3 data-id="element-259">Email support</h3>
                  <p data-id="element-260">A direct line to the team building Andoe.</p>
                </div>
              </div>
            </div>
          </section>

          {/* END CTA */}
          <section className="end-cta" id="start" data-id="element-261">
            <div className="end-label" data-id="element-262">Next</div>
            <h2 data-id="element-263">Hire your AI search analyst.</h2>
            <p data-id="element-264">
              We are onboarding a small group of beta partners. Reach out and we
              will set up your workspace, run your first weekly visibility
              report, and walk you through the to-do list.
            </p>
            <span className="cta is-disabled" aria-disabled="true" data-id="element-265">
              Deactivated
            </span>
          </section>

          <Footer data-id="element-266" />
        </article>

        <pre className="json-view" dangerouslySetInnerHTML={{
        __html: jsonHtml
      }} data-id="element-267" />
      </main>
    </>;
}